import {
  connectCurrentRefinements,
  connectToggleRefinement,
} from 'instantsearch.js/es/connectors';
import { currentRefinementsClasses, texts } from './common.js';

const cls = currentRefinementsClasses;
const deleteIcon =
  '<svg class="w-3.5 h-3.5" aria-hidden="true"><use href="#icon-x-mark-solid"></use></svg>';

/**
 * Current refinements — rendered manually via connector so we can
 * inject sprite SVG icons and unify chip markup.
 */
export function createCurrentRefinements(
  containerSelector = '[data-current-refinements]',
) {
  const container = document.querySelector(containerSelector);

  if (!container) {
    return null;
  }

  const list = document.createElement('ul');
  list.className = `ais-CurrentRefinements-list ${cls.list.join(' ')}`;
  container.replaceChildren(list);

  // Build an attribute -> translated label map from configured widgets.
  const attributeLabelMap = new Map(
    Array.from(document.querySelectorAll('[data-widget-auto][data-attribute]'))
      .map((el) => [el.dataset.attribute, el.dataset.label])
      .filter(([attribute, label]) => attribute && label),
  );

  return connectCurrentRefinements(({ items, refine }) => {
    const filtered = items.filter((item) => item.attribute !== 'legacy');
    list.replaceChildren();

    filtered.forEach((item) => {
      const attributeLabel =
        attributeLabelMap.get(item.attribute) || formatLabel(item.label);
      const li = document.createElement('li');
      li.className = `ais-CurrentRefinements-item ${cls.item.join(' ')}`;

      const labelEl = document.createElement('span');
      labelEl.className = cls.label.join(' ');
      labelEl.textContent = `${attributeLabel}:`;
      li.appendChild(labelEl);

      item.refinements.forEach((refinement, i) => {
        if (i > 0) {
          const sep = document.createElement('span');
          sep.className = 'block w-px h-3.5 bg-gray-300';
          sep.setAttribute('aria-hidden', 'true');
          li.appendChild(sep);
        }

        const valueEl = document.createElement('span');
        valueEl.className = `inline-flex items-center gap-0.5 ${cls.category.join(' ')}`;
        valueEl.innerHTML = `
                    <span class="js-apply-italic-i">${refinement.label}</span>
                    <button type="button" class="${cls.delete.join(' ')}" aria-label="${texts.remove} ${refinement.label}">
                        ${deleteIcon}
                    </button>
                `;
        valueEl.querySelector('button').addEventListener('click', () => {
          refine(refinement);
        });
        li.appendChild(valueEl);
      });

      list.appendChild(li);
    });
  })({});
}

/**
 * Create a custom widget that shows a chip in current refinements
 * when a toggle with data-checked="true" is unchecked
 * @param {string} containerSelector - CSS selector for the current refinements container
 * @returns {Object|null} Custom toggle indicator widget or null
 */
export function createLegacyToggleIndicator(
  containerSelector = '[data-current-refinements]',
) {
  const container = document.querySelector(containerSelector);
  const toggleWidget = document.querySelector(
    '[data-widget-type="toggleRefinement"][data-unchecked-chip-label]',
  );

  if (!container || !toggleWidget) {
    return null;
  }

  const attribute = toggleWidget.dataset.attribute;
  const chipLabel = toggleWidget.dataset.uncheckedChipLabel;
  const onValue =
    toggleWidget.dataset.on === 'true'
      ? true
      : toggleWidget.dataset.on === 'false'
        ? false
        : toggleWidget.dataset.on;
  const chipId = `${attribute}-toggle-chip`;

  return connectToggleRefinement(({ value, refine }) => {
    // Remove existing chip if present
    const existingChip = document.getElementById(chipId);
    if (existingChip) {
      existingChip.remove();
    }

    // If toggle is OFF (not refined), show the chip
    if (!value.isRefined) {
      const list = container.querySelector('.ais-CurrentRefinements-list');
      const targetContainer = list || container;

      const chip = document.createElement('li');
      chip.id = chipId;
      chip.className = cls.item.join(' ');
      chip.innerHTML = `
                <span class="${cls.category.join(' ')}">${chipLabel}</span>
                <button type="button" class="${cls.delete.join(' ')}" aria-label="${texts.remove}">
                    ${deleteIcon}
                </button>
            `;

      // Clicking the chip re-enables the filter
      chip.querySelector('button').addEventListener('click', () => {
        refine({ isRefined: false });
      });

      targetContainer.appendChild(chip);
    }
  })({
    attribute,
    on: onValue,
  });
}

/**
 * Format facet labels for display
 * @param {string} label - Raw label
 * @returns {string} Formatted label
 */
function formatLabel(label) {
  // Convert camelCase to Title Case
  return label
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}
